import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  HiDownload,
  HiLocationMarker,
  HiPhone,
  HiShieldCheck,
  HiX,
} from 'react-icons/hi';
import { toPng } from 'html-to-image';
import apiService from '../services/api';
import {
  getPickupContact,
  getPickupDestinationLabel,
  getPickupLocationText,
} from '../utils/pickupLocation';
import './PickupInfo.css';

const STATUS_LABELS = {
  pendente: 'Pendente',
  enviada: 'Enviada',
  aguardando_confirmacao: 'Aguardando confirmação',
  incompleto: 'Pagamento incompleto',
  paga: 'Paga',
  cancelada: 'Cancelada',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value) || 0
  );

/** Converte "DISPONÍVEL PARA RETIRADA" → "Disponível para retirada" */
const formatStatusTitle = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const lower = raw.replace(/[_-]+/g, ' ').toLowerCase();
  return lower.replace(/(^|\s)\S/g, (m) => m.toUpperCase());
};

const normalizeChargeBundle = (data) => {
  if (!data) return null;
  const charge = data.charge || null;
  return {
    charge,
    clientName: data.clientName || data.client_name || charge?.cliente_nome || 'Cliente',
    merchantName: data.merchantName || data.merchant_name || 'SENDINGTO',
    whatsappNumber: data.whatsappNumber || data.whatsapp_number || '',
    pacote: data.pacote || null,
    status_pagamento: data.status_pagamento || (charge?.status === 'paga' ? 'Pago' : null),
    valor_total: data.valor_total,
    valor_pago: data.valor_pago,
    valor_restante: data.valor_restante,
    pode_gerar_cobranca:
      data.pode_gerar_cobranca != null
        ? Boolean(data.pode_gerar_cobranca)
        : !charge,
  };
};

const normalizeTrackingBundle = (json) => {
  const data = json?.data || json || {};
  const message = String(json?.message || '');
  const historico = Array.isArray(data.historico) ? data.historico : [];
  const vinculadoRemessa =
    message === 'Histórico obtido com sucesso' ||
    (Boolean(data.status_atual) &&
      !data.localizacao_origem &&
      data.pacote != null &&
      data.pacote.status == null);

  return {
    statusAtual: data.status_atual || data.pacote?.status || '',
    historico,
    vinculadoRemessa,
    codigoPacote: data.pacote?.codigo_pacote || '',
    nomeDestinatario:
      data.pacote?.nome_destinatario || data.cliente_destinatario?.nome || '',
    destino: data.pacote?.destino || '',
    message,
  };
};

const PickupInfo = () => {
  const [searchParams] = useSearchParams();
  const cardRef = useRef(null);
  const [savingImage, setSavingImage] = useState(false);
  const [feedback, setFeedback] = useState('');

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [copiedPix, setCopiedPix] = useState(false);

  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const info = useMemo(
    () => ({
      clientCode: searchParams.get('codigo') || '',
      cobrancaId: (
        searchParams.get('cobranca') ||
        searchParams.get('chg') ||
        ''
      ).trim(),
      destinatario: (searchParams.get('destinatario') || '').trim(),
      destino: (searchParams.get('destino') || '').trim(),
    }),
    [searchParams]
  );

  const recipientName =
    paymentData?.pacote?.nome_destinatario ||
    trackingData?.nomeDestinatario ||
    info.destinatario ||
    '';
  const destination =
    paymentData?.pacote?.destino ||
    trackingData?.destino ||
    info.destino ||
    '';

  const pickupLocationText = useMemo(
    () => getPickupLocationText(destination),
    [destination]
  );
  const pickupDestinationLabel = useMemo(
    () => getPickupDestinationLabel(destination),
    [destination]
  );
  const pickupContact = useMemo(
    () => getPickupContact(destination),
    [destination]
  );

  useEffect(() => {
    const root = document.documentElement;
    const previousScheme = root.style.colorScheme;
    const previousHtmlBg = root.style.backgroundColor;
    root.style.colorScheme = 'only light';
    root.style.backgroundColor = '#eef6f8';

    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute('content');
    if (themeMeta) themeMeta.setAttribute('content', '#eef6f8');

    let schemeMeta = document.querySelector('meta[name="color-scheme"]');
    let createdSchemeMeta = false;
    if (!schemeMeta) {
      schemeMeta = document.createElement('meta');
      schemeMeta.setAttribute('name', 'color-scheme');
      document.head.appendChild(schemeMeta);
      createdSchemeMeta = true;
    }
    schemeMeta.setAttribute('content', 'only light');

    let wakeLock = null;
    let cancelled = false;
    const holdScreenBright = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const screenBrightness = window.screen;
      if (screenBrightness && 'brightness' in screenBrightness) {
        try {
          screenBrightness.brightness = 1;
        } catch (error) {
          void error;
        }
      }
      if (!navigator.wakeLock?.request) return;
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch {
        wakeLock = null;
      }
    };
    holdScreenBright();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') holdScreenBright();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      root.style.colorScheme = previousScheme;
      root.style.backgroundColor = previousHtmlBg;
      if (themeMeta) {
        themeMeta.setAttribute('content', previousTheme || '#000000');
        themeMeta.removeAttribute('data-pickup-light');
      }
      if (createdSchemeMeta) schemeMeta.remove();
      else if (schemeMeta) schemeMeta.remove();
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLock) wakeLock.release().catch(() => null);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadFromCobrancaId(cobrancaId) {
      const json = await apiService.getCobrancaPublic(cobrancaId);
      return normalizeChargeBundle(json?.data || json);
    }

    async function loadFromCodigoCliente(codigo) {
      const json = await apiService.getCobrancaRetiradaPublic(codigo);
      return normalizeChargeBundle(json?.data || json);
    }

    async function loadPayment() {
      if (!info.clientCode && !info.cobrancaId) {
        setPaymentData(null);
        return;
      }

      setPaymentLoading(true);
      setPaymentError('');
      setShowPayModal(false);

      try {
        let bundle = null;

        if (info.clientCode) {
          try {
            bundle = await loadFromCodigoCliente(info.clientCode);
          } catch {
            if (!info.cobrancaId) {
              if (cancelled) return;
              setPaymentData({
                charge: null,
                pode_gerar_cobranca: true,
                status_pagamento: null,
                valor_restante: null,
                merchantName: 'SENDINGTO',
              });
              setPaymentError('');
              return;
            }
          }
        }

        if (info.cobrancaId) {
          try {
            const fromCharge = await loadFromCobrancaId(info.cobrancaId);
            if (cancelled) return;
            if (fromCharge?.charge) {
              setPaymentData({
                ...(bundle || {}),
                ...fromCharge,
                pacote: bundle?.pacote || fromCharge.pacote || null,
                pode_gerar_cobranca: false,
              });
              return;
            }
          } catch (err) {
            if (!info.clientCode) {
              if (!cancelled) {
                setPaymentData(null);
                setPaymentError(err?.message || 'Cobrança não encontrada');
              }
              return;
            }
          }
        }

        if (cancelled) return;
        if (bundle) {
          setPaymentData(bundle);
        }
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    }

    loadPayment();
    return () => {
      cancelled = true;
    };
  }, [info.clientCode, info.cobrancaId]);

  useEffect(() => {
    let cancelled = false;

    async function loadTracking() {
      if (!info.clientCode) {
        setTrackingData(null);
        return;
      }
      setTrackingLoading(true);
      try {
        const json = await apiService.getTrackingByClientCode(info.clientCode);
        if (cancelled) return;
        setTrackingData(normalizeTrackingBundle(json));
      } catch {
        if (!cancelled) setTrackingData(null);
      } finally {
        if (!cancelled) setTrackingLoading(false);
      }
    }

    loadTracking();
    return () => {
      cancelled = true;
    };
  }, [info.clientCode]);

  const charge = paymentData?.charge || null;
  const paymentUrl = useMemo(() => {
    if (!charge?.id) return '';
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/pagar/${charge.id}`;
    }
    return charge.payment_url || '';
  }, [charge]);

  const canGenerate = Boolean(paymentData?.pode_gerar_cobranca) && !charge;
  const isPaidPackage =
    paymentData?.status_pagamento === 'Pago' || charge?.status === 'paga';
  const showPaymentSection =
    Boolean(info.clientCode || info.cobrancaId) &&
    (paymentLoading || paymentData || paymentError);

  const statusLabel = trackingData?.vinculadoRemessa
    ? 'Status da Remessa'
    : 'Status do Pacote';
  const statusAtual = trackingData?.statusAtual || '';
  const historico = trackingData?.historico || [];
  const showStatusSection =
    Boolean(info.clientCode) && (trackingLoading || statusAtual || historico.length > 0);

  const handleDownloadPng = async () => {
    const node = cardRef.current;
    if (!node) return;

    try {
      setSavingImage(true);
      setFeedback('');
      node.classList.add('pickup-card--exporting');
      // Aguarda o layout aplicar o hide do card de pagamento
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
        filter: (nodeEl) => {
          if (!(nodeEl instanceof HTMLElement)) return true;
          if (
            nodeEl.classList.contains('pickup-pay') ||
            nodeEl.closest?.('.pickup-pay')
          ) {
            return false;
          }
          return (
            !nodeEl.classList.contains('pickup-card__actions') &&
            !nodeEl.classList.contains('pickup-card__feedback') &&
            !nodeEl.classList.contains('pickup-card__brand-link') &&
            !nodeEl.classList.contains('pickup-status__more')
          );
        },
      });
      const code = (info.clientCode || 'retirada').replace(/[^\w-]/g, '_').slice(0, 40);
      const link = document.createElement('a');
      link.download = `retirada-${code}.png`;
      link.href = dataUrl;
      link.click();
      setFeedback('Imagem baixada com sucesso.');
    } catch (err) {
      setFeedback(err?.message || 'Não foi possível gerar a imagem. Tente novamente.');
    } finally {
      node.classList.remove('pickup-card--exporting');
      setSavingImage(false);
    }
  };

  const handleGerarPix = async () => {
    if (!info.clientCode || generating) return;
    setGenerating(true);
    setPaymentError('');
    try {
      const json = await apiService.gerarCobrancaRetiradaPublic(info.clientCode);
      const data = normalizeChargeBundle(json?.data || json);
      setPaymentData((prev) => ({
        ...(prev || {}),
        ...data,
        pode_gerar_cobranca: false,
      }));
      setShowPayModal(true);
    } catch (err) {
      const msg = String(err?.message || '');
      const unavailable =
        /erro no servidor|not found|404|route/i.test(msg) ||
        msg.toLowerCase().includes('não foi possível');
      setPaymentError(
        unavailable
          ? 'Não foi possível gerar o PIX agora. A API de cobrança na retirada precisa estar publicada, ou peça ao suporte para gerar a cobrança.'
          : msg || 'Não foi possível gerar o pagamento PIX.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const openPayModal = () => {
    if (!charge) return;
    setShowPayModal(true);
  };

  const copyPixCode = async () => {
    const text = charge?.pix_payload;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    } catch {
      setFeedback('Não foi possível copiar. Tente novamente.');
    }
  };

  const renderPaymentBlock = () => {
    if (!showPaymentSection) return null;

    if (paymentLoading) {
      return (
        <section className="pickup-pay">
          <p className="pickup-pay__muted">Verificando pagamento…</p>
        </section>
      );
    }

    if (isPaidPackage && (!charge || charge.status === 'paga')) {
      return (
        <section className="pickup-pay">
          <div className="pickup-pay__alert pickup-pay__alert--success">
            <strong>Tudo certo com o pagamento</strong>
            <p>
              Agradecemos a confiança. Sua carga está quitada e pronta para a retirada
              com tranquilidade.
            </p>
          </div>
        </section>
      );
    }

    if (charge) {
      const isPaid = charge.status === 'paga';
      const isAwaiting = charge.status === 'aguardando_confirmacao';
      const isIncomplete = charge.status === 'incompleto';
      const isCancelled = charge.status === 'cancelada';
      const isPendingPayment =
        charge.status === 'pendente' || charge.status === 'enviada';
      const valorRestante =
        charge.valor_restante != null
          ? Number(charge.valor_restante)
          : Math.max(0, (Number(charge.valor) || 0) - (Number(charge.valor_pago) || 0));
      const valorPago = Number(charge.valor_pago) || 0;
      const showPayAction = !isPaid && !isCancelled;

      return (
        <section className="pickup-pay">
          <div className="pickup-pay__head">
            <span className="pickup-pay__eyebrow">Pagamento PIX</span>
            <h2>{charge.titulo || 'Cobrança SendingTo'}</h2>
            <span className={`pickup-pay__status pickup-pay__status--${charge.status}`}>
              {STATUS_LABELS[charge.status] || charge.status}
            </span>
          </div>

          <div className="pickup-pay__amount">
            <small>
              {isIncomplete
                ? 'Restante a pagar'
                : isPendingPayment
                  ? 'Valor pendente'
                  : 'Valor a pagar'}
            </small>
            <strong>{formatCurrency(isIncomplete ? valorRestante : charge.valor)}</strong>
            {isIncomplete || valorPago > 0 ? (
              <span>
                Total {formatCurrency(charge.valor)} · Já pago {formatCurrency(valorPago)}
              </span>
            ) : null}
          </div>

          {isPaid ? (
            <div className="pickup-pay__alert pickup-pay__alert--success">
              <strong>Tudo certo com o pagamento</strong>
              <p>
                Agradecemos a confiança. Sua carga está quitada e pronta para a retirada
                com tranquilidade.
              </p>
            </div>
          ) : null}

          {isPendingPayment ? (
            <div className="pickup-pay__alert pickup-pay__alert--warn">
              <strong>Pagamento pendente</strong>
              <p>
                Há um valor em aberto para esta carga. Conclua o pagamento via PIX para
                liberar a retirada.
              </p>
            </div>
          ) : null}

          {isAwaiting ? (
            <div className="pickup-pay__alert pickup-pay__alert--warn">
              <strong>Aguardando confirmação</strong>
              <p>Recebemos o aviso de pagamento. A confirmação final é feita pela SendingTo.</p>
            </div>
          ) : null}

          {isIncomplete ? (
            <div className="pickup-pay__alert pickup-pay__alert--warn">
              <strong>Pagamento incompleto</strong>
              <p>
                Já recebemos {formatCurrency(valorPago)}. Ainda falta{' '}
                {formatCurrency(valorRestante)}.
              </p>
            </div>
          ) : null}

          {showPayAction ? (
            <button
              type="button"
              className="pickup-action-btn pickup-action-btn--primary pickup-action-btn--full"
              onClick={openPayModal}
            >
              Pagar com PIX
            </button>
          ) : null}
        </section>
      );
    }

    if (canGenerate || paymentError) {
      return (
        <section className="pickup-pay">
          <div className="pickup-pay__head">
            <span className="pickup-pay__eyebrow">Pagamento PIX</span>
            <h2>Pague sua carga</h2>
            <p className="pickup-pay__muted">
              Gere o pagamento PIX para quitar o valor pendente.
            </p>
          </div>
          {(paymentData?.valor_restante ?? 0) > 0 ? (
            <div className="pickup-pay__amount">
              <small>Valor pendente</small>
              <strong>{formatCurrency(paymentData.valor_restante)}</strong>
            </div>
          ) : null}
          {paymentError ? <p className="pickup-pay__error">{paymentError}</p> : null}
          {info.clientCode ? (
            <button
              type="button"
              className="pickup-action-btn pickup-action-btn--primary pickup-action-btn--full"
              onClick={handleGerarPix}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="pickup-spinner" aria-hidden />
                  Gerando…
                </>
              ) : (
                'Pagar com PIX'
              )}
            </button>
          ) : null}
        </section>
      );
    }

    return null;
  };

  const renderPayModal = () => {
    if (!showPayModal || !charge) return null;

    const isIncomplete = charge.status === 'incompleto';
    const valorRestante =
      charge.valor_restante != null
        ? Number(charge.valor_restante)
        : Math.max(0, (Number(charge.valor) || 0) - (Number(charge.valor_pago) || 0));
    const displayValor = isIncomplete ? valorRestante : charge.valor;
    const pixPayload = charge.pix_payload || '';
    const merchantName = paymentData?.merchantName || 'SENDINGTO';
    const clientName = paymentData?.clientName || recipientName || 'Cliente';

    return (
      <div
        className="pickup-pay-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pickup-pay-modal-title"
        onClick={() => setShowPayModal(false)}
      >
        <div
          className="pickup-pay-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="pickup-pay-modal__header">
            <div>
              <p className="pickup-pay-modal__eyebrow">Cobrança PIX</p>
              <h2 id="pickup-pay-modal-title">
                {charge.titulo || 'Pagamento SendingTo'}
              </h2>
              <p className="pickup-pay-modal__sub">{clientName}</p>
            </div>
            <button
              type="button"
              className="pickup-pay-modal__close"
              onClick={() => setShowPayModal(false)}
              aria-label="Fechar"
            >
              <HiX size={20} />
            </button>
          </header>

          <div className="pickup-pay-modal__amount">
            <small>{isIncomplete ? 'Restante a pagar' : 'Valor a pagar'}</small>
            <strong>{formatCurrency(displayValor)}</strong>
            <span className={`pickup-pay__status pickup-pay__status--${charge.status}`}>
              {STATUS_LABELS[charge.status] || charge.status}
            </span>
          </div>

          <p className="pickup-pay-modal__hint">
            Escaneie o QR Code ou copie o código no app do seu banco.
          </p>

          <div className="pickup-pay-modal__pix">
            <div className="pickup-pay-modal__qr">
              {pixPayload ? (
                <QRCodeSVG
                  value={pixPayload}
                  size={168}
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  level="M"
                />
              ) : (
                <span>QR Code indisponível</span>
              )}
            </div>

            <div className="pickup-pay-modal__details">
              <div>
                <small>Recebedor</small>
                <strong>{merchantName}</strong>
              </div>
              <div>
                <small>PIX Copia e Cola</small>
                <p className="pickup-pay-modal__code">{pixPayload || '—'}</p>
              </div>
              <button
                type="button"
                className="pickup-action-btn pickup-action-btn--pix"
                onClick={copyPixCode}
                disabled={!pixPayload}
              >
                {copiedPix ? 'Copiado!' : 'Copiar código PIX'}
              </button>
            </div>
          </div>

          {paymentUrl ? (
            <a
              className="pickup-pay-modal__full-link"
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir página completa de pagamento
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!showHistoryModal) return null;

    return (
      <div
        className="pickup-pay-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pickup-history-modal-title"
        onClick={() => setShowHistoryModal(false)}
      >
        <div className="pickup-history-modal" onClick={(e) => e.stopPropagation()}>
          <header className="pickup-pay-modal__header">
            <div>
              <p className="pickup-pay-modal__eyebrow">
                {trackingData?.vinculadoRemessa ? 'Remessa / Invoice' : 'Pacote'}
              </p>
              <h2 id="pickup-history-modal-title">Histórico de status</h2>
              {trackingData?.codigoPacote ? (
                <p className="pickup-pay-modal__sub">
                  Carga {trackingData.codigoPacote}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="pickup-pay-modal__close"
              onClick={() => setShowHistoryModal(false)}
              aria-label="Fechar"
            >
              <HiX size={20} />
            </button>
          </header>

          {historico.length === 0 ? (
            <p className="pickup-pay__muted">Nenhum histórico disponível.</p>
          ) : (
            <ol className="pickup-timeline">
              {historico.map((item, idx) => (
                <li key={`${item.data}-${item.hora}-${idx}`} className="pickup-timeline__item">
                  <span className="pickup-timeline__dot" aria-hidden />
                  <div className="pickup-timeline__body">
                    <div className="pickup-timeline__meta">
                      <strong>{formatStatusTitle(item.status)}</strong>
                      <span>
                        {[item.data, item.hora].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </div>
                    {(item.local_pacote || item.local) ? (
                      <p className="pickup-timeline__local">
                        {item.local_pacote || item.local}
                      </p>
                    ) : null}
                    {item.descricao ? (
                      <p className="pickup-timeline__desc">{item.descricao}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="pickup-page">
      <div className="pickup-page__glow pickup-page__glow--one" />
      <div className="pickup-page__glow pickup-page__glow--two" />

      <article className="pickup-card" ref={cardRef}>
        <header className="pickup-card__header">
          <img src="/SENDING_TO.png" alt="SendingTo Express" className="pickup-card__logo" />
          <span className="pickup-card__secure">
            <HiShieldCheck size={18} /> Retirada segura
          </span>
        </header>

        <section className="pickup-card__intro">
          <span className="pickup-card__eyebrow">Informações de retirada</span>
          <h1>{recipientName ? `Olá, ${recipientName}` : 'Olá'}</h1>
          <p>Apresente este QR Code no local para identificar e retirar sua carga.</p>
        </section>

        <section className="pickup-card__qr-section">
          <div className="pickup-card__qr">
            {info.clientCode ? (
              <QRCodeSVG
                value={info.clientCode}
                size={220}
                level="M"
                includeMargin={false}
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            ) : (
              <span>QR Code indisponível</span>
            )}
          </div>
          <div className="pickup-card__code">
            <small>Código do cliente</small>
            <strong>{info.clientCode || 'Não informado'}</strong>
          </div>
        </section>

        {showStatusSection ? (
          <section className="pickup-status">
            <div className="pickup-status__row">
              <div>
                <small>{statusLabel}</small>
                <strong>
                  {trackingLoading
                    ? 'Carregando…'
                    : formatStatusTitle(statusAtual) || 'Indisponível'}
                </strong>
              </div>
              {!trackingLoading && historico.length > 0 ? (
                <button
                  type="button"
                  className="pickup-status__more"
                  onClick={() => setShowHistoryModal(true)}
                >
                  Ver mais
                </button>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="pickup-card__details">
          <div className="pickup-detail">
            <span className="pickup-detail__icon">
              <HiLocationMarker size={22} />
            </span>
            <div>
              <small>Local da retirada — {pickupDestinationLabel}</small>
              <strong>{pickupLocationText}</strong>
            </div>
          </div>

          {pickupContact ? (
            <a className="pickup-detail pickup-detail--contact" href={`tel:${pickupContact.tel}`}>
              <span className="pickup-detail__icon">
                <HiPhone size={21} />
              </span>
              <div>
                <small>Contato no local</small>
                <strong>{pickupContact.name}</strong>
                <span>{pickupContact.phone}</span>
              </div>
            </a>
          ) : null}
        </section>

        {renderPaymentBlock()}

        <div className="pickup-card__actions">
          <button
            type="button"
            className="pickup-action-btn pickup-action-btn--download"
            onClick={handleDownloadPng}
            disabled={savingImage}
          >
            <HiDownload size={16} />
            {savingImage ? 'Gerando PNG…' : 'Baixar Etiqueta'}
          </button>
        </div>

        {feedback ? <p className="pickup-card__feedback">{feedback}</p> : null}

        <p className="pickup-card__note">
          Guarde este link e tenha o código em mãos quando chegar ao local.
        </p>
        <Link to="/login" className="pickup-card__brand-link">
          SendingTo Express
        </Link>
      </article>

      {renderPayModal()}
      {renderHistoryModal()}
    </main>
  );
};

export default PickupInfo;
